import { Observable, catchError, map, of } from 'rxjs'
import { Injectable } from '@angular/core'

import { IAlgorithmConfig, IAlgorithmDefaults, IAlgorithmParams, IPreviewProfile, ISimulationResult } from '../../interfaces/algorithm-config.interface'
import { ApiClass } from '@data/class/api-class'
import { environment } from '@env/environment'

interface IEnvelope<T> { error: boolean; msg: string; data: T }

/** Respuesta normalizada: el componente ramifica por `error` y muestra `msg` tal cual. */
export interface IApiResult<T> { error: boolean; msg: string; code?: string; errors?: string[]; data: T | null }

/**
 * El saldo de créditos del proveedor de respaldo.
 *
 * El tipo conserva el nombre inglés `Wallet` porque es como lo llama la API de Ekatena; en la
 * interfaz se dice **créditos**, porque «cartera» en esta plataforma es la de arrendamientos.
 *
 * `available: false` **no** es un saldo de cero: es que no se pudo saber. La pantalla los distingue
 * porque llevan a decisiones opuestas — con cero se apaga el respaldo por falta de fondos, con
 * desconocido no se concluye nada.
 */
export interface IEkatenaWallet {
  available: boolean
  reason?: 'not_configured' | 'unsupported_environment' | 'unreachable'
  balance: number | null
  liveScores: number | null
  preVerifications: number | null
  checkedAt: string
  organizationName: string | null
}

@Injectable({ providedIn: 'root' })
export class ApiAlgorithmConfigService extends ApiClass {
  constructor() {
    super()
    this.path = 'algorithm-config'
  }

  //* El backend responde con códigos machine-readable (`INVALID_ALGORITHM_PARAMS`, `NOT_A_DRAFT`,
  //* `CONCURRENT_ACTIVATION`) y, en el caso de validación, con la lista de problemas concretos.
  //* Se propagan tal cual: el mensaje del servidor explica el porqué mejor que un genérico del front.
  private handle<T>(obs: Observable<IEnvelope<T>>): Observable<IApiResult<T>> {
    return obs.pipe(
      map((r) => ({ error: false, msg: r.msg, data: r.data })),
      catchError((e) => {
        //* `code` y `errors` viajan en el nivel SUPERIOR del envelope, no dentro de `data`: el
        //* filtro global de excepciones sustituye `data` por su propio diagnóstico de la request.
        const env = e?.error ?? {}
        return of({
          error: true,
          msg: env?.msg || 'Hubo un error de conexión',
          code: env?.code,
          errors: Array.isArray(env?.errors) ? env.errors : undefined,
          data: null,
        } as IApiResult<T>)
      })
    )
  }

  /**
   * El saldo vive en el módulo `verification`, no en `algorithm-config`: la dependencia entre módulos
   * del backend va en una sola dirección y meterlo allá crearía un ciclo. Desde el front da igual —se
   * pide donde esté— y por eso la URL no usa `this.path`.
   */
  getEkatenaWallet(refrescar = false): Observable<IApiResult<IEkatenaWallet>> {
    const q = refrescar ? '?refresh=1' : ''
    return this.handle(this.http.get<IEnvelope<IEkatenaWallet>>(`${environment.apiUrl}verification/providers/ekatena/wallet${q}`))
  }

  getAll(): Observable<IApiResult<IAlgorithmConfig[]>> {
    return this.handle(this.http.get<IEnvelope<IAlgorithmConfig[]>>(`${environment.apiUrl}${this.path}`))
  }

  getActive(): Observable<IApiResult<IAlgorithmConfig | null>> {
    return this.handle(this.http.get<IEnvelope<IAlgorithmConfig | null>>(`${environment.apiUrl}${this.path}/active`))
  }

  /** Valores de arranque, rangos por campo y guardarraíles. Evita duplicar las reglas en el front. */
  getDefaults(): Observable<IApiResult<IAlgorithmDefaults>> {
    return this.handle(this.http.get<IEnvelope<IAlgorithmDefaults>>(`${environment.apiUrl}${this.path}/defaults`))
  }

  getById(id: string): Observable<IApiResult<IAlgorithmConfig>> {
    return this.handle(this.http.get<IEnvelope<IAlgorithmConfig>>(`${environment.apiUrl}${this.path}/${id}`))
  }

  create(body: { name: string; description?: string; changeReason: string; params: IAlgorithmParams; derivedFromId?: string }): Observable<IApiResult<IAlgorithmConfig>> {
    return this.handle(this.http.post<IEnvelope<IAlgorithmConfig>>(`${environment.apiUrl}${this.path}`, body))
  }

  update(id: string, body: { name?: string; description?: string; changeReason?: string; params?: IAlgorithmParams }): Observable<IApiResult<IAlgorithmConfig>> {
    return this.handle(this.http.put<IEnvelope<IAlgorithmConfig>>(`${environment.apiUrl}${this.path}/${id}`, body))
  }

  activate(id: string): Observable<IApiResult<IAlgorithmConfig>> {
    return this.handle(this.http.post<IEnvelope<IAlgorithmConfig>>(`${environment.apiUrl}${this.path}/${id}/activate`, {}))
  }

  remove(id: string): Observable<IApiResult<{ deleted: boolean }>> {
    return this.handle(this.http.delete<IEnvelope<{ deleted: boolean }>>(`${environment.apiUrl}${this.path}/${id}`))
  }

  /** Corre los perfiles arquetipo con unos parámetros. No lee la base ni llama a Syntage. */
  preview(params: IAlgorithmParams): Observable<IApiResult<IPreviewProfile[]>> {
    return this.handle(this.http.post<IEnvelope<IPreviewProfile[]>>(`${environment.apiUrl}${this.path}/preview`, { params }))
  }

  /** Recalcula verificaciones reales con una configuración candidata. No persiste nada. */
  simulate(body: { configId?: string; params?: IAlgorithmParams; sampleSize?: number }): Observable<IApiResult<ISimulationResult>> {
    return this.handle(this.http.post<IEnvelope<ISimulationResult>>(`${environment.apiUrl}${this.path}/simulate`, body))
  }
}
